import { AxiosResponse } from 'axios';
import { useMutation, useQuery } from 'react-query';
import apiClient from '../apiClient/apiClient';
import {
  AddRawProdsToRecipesRequestBody,
  AddRawProdsToRecipesResponse, CompareRecipeDetails,
  CopyRecipeRequestBody, CreateRecipeDetailRequest, CreateRecipeRequest, DeleteMultipleRecipes,
  DeleteRecipesResponse,
  RawProdAssignedToRecipe,
  Recipe, RecipeDetail, RecipeDetailKey, RecipeKey,
  RecipeRawProdsRequestBody,
  RecipeRequestBody,
  RemoveRawProdsToRecipesRequestBody,
  RemoveRawProdsToRecipesResponse, ReplaceRecipeDetailsRequest, UpdateRecipeRequest
} from './recipe.types';

export const useGetRecipes = (
    requestBody?: {
      classId: number,
      baseId: number,
      sizeId: number,
      productId: number,
      status: number
    }
) => {
  return useQuery<AxiosResponse<Recipe[]>>({
    queryKey: [
      'getRecipes',
      requestBody?.classId,
      requestBody?.baseId,
      requestBody?.sizeId,
      requestBody?.productId,
      requestBody?.status
    ],
    queryFn: () =>
        apiClient.get<Recipe[]>('/recipes', { params: requestBody }),
  });
};

export const useCreateRecipe = () => {
  return useMutation({
    mutationKey: ['createRecipe'],
    mutationFn: (requestBody: CreateRecipeRequest) =>
        apiClient.post<Recipe>('/recipes', requestBody)
  })
};

export const useGetRecipe = (
    {
      recipeId
    }: RecipeKey,
    enabled: boolean = true
) => {
  return useQuery<AxiosResponse<Recipe>>({
    queryKey: ['getRecipe', recipeId],
    queryFn: () =>
        apiClient.get<Recipe>(`/recipes/${recipeId}`),
    enabled
  });
};

export const useUpdateRecipe = (
    {
      recipeId
    }: RecipeKey
) => {
  return useMutation({
    mutationKey: ['updateRecipe', recipeId],
    mutationFn: (requestBody: UpdateRecipeRequest) =>
        apiClient.put<Recipe>(`/recipes/${recipeId}`, requestBody)
  })
};

export const useDeleteRecipe = (
    {
      recipeId
    }: RecipeKey
) => {
  return useMutation({
    mutationKey: ['deleteRecipe', recipeId],
    mutationFn: () =>
        apiClient.delete<Recipe>(`/recipes/${recipeId}`)
  })
};

export const useGetRecipeDetails = (
    {
      recipeId
    }: RecipeKey,
    params?: {
      rawProductId?: number
    }
) => {
  return useQuery<AxiosResponse<RecipeDetail[]>>({
    queryKey: ['getRecipeDetails', recipeId, params],
    queryFn: () =>
        apiClient.get<RecipeDetail[]>(`/recipes/${recipeId}/details`, { params })
  })
}

export const useCreateRecipeDetail = (
    {
      recipeId
    }: RecipeKey
) => {
  return useMutation({
    mutationKey: ['createRecipeDetail', recipeId],
    mutationFn: (requestBody: CreateRecipeDetailRequest) =>
        apiClient.post<RecipeDetail>(`/recipes/${recipeId}/details`, requestBody)
  })
}

export const useReplaceRecipeDetails = (
    {
      recipeId
    }: RecipeKey
) => {
  return useMutation({
    mutationKey: ['updateRecipeDetails', recipeId],
    mutationFn: (requestBody: ReplaceRecipeDetailsRequest) =>
        apiClient.put<RecipeDetail[]>(`/recipes/${recipeId}/details`, requestBody)
  })
}

export const useGetRecipeDetail = (
    {
      recipeId,
      rawProductId
    }: RecipeDetailKey
) => {
  return useQuery<AxiosResponse<RecipeDetail>>({
    queryKey: ['getRecipeDetail', recipeId, rawProductId],
    queryFn: () =>
        apiClient.get<RecipeDetail>(`/recipes/${recipeId}/details/${rawProductId}`)
  })
}

export const useFmsProdsReport = (enabled: boolean = false) => {
  return useQuery<AxiosResponse<number[]>>({
    queryKey: ['fmsprod', 'report'],
    queryFn: () => apiClient.get<number[]>(`/recipes/fmsprod-report`),
    enabled,
  });
};

export const useRecipeDetails = (specNo: string, enabled: boolean = false) => {
  return useQuery<AxiosResponse<RawProdAssignedToRecipe[]>>({
    queryKey: ['recipeDetails', specNo],
    queryFn: () => apiClient.get<RawProdAssignedToRecipe[]>(`/recipes/${specNo}/details`),
    enabled,
  });
};

export const useCompareRecipeDetails = (specNo1: string,specNo2: string, enabled: boolean = false) => {
  return useQuery<AxiosResponse<CompareRecipeDetails[]>>({
    queryKey: ['recipeCompareDetails',specNo1,specNo2],
    queryFn: () => apiClient.get<CompareRecipeDetails[]>(`/recipes/compare/${specNo1}/${specNo2}`),
    enabled,
  });
};


export const useEditRecipe = (specNo: string) => {
  return useMutation((record: RecipeRequestBody) => {
    return apiClient.put(`/recipes/${specNo}`, record);
  });
};

export const useEditRecipeRawProds = () => {
  return useMutation((record: RecipeRawProdsRequestBody) => {
    return apiClient.put(`/recipes/details`, record);
  });
}

export const useRemoveRecipeRawProds = () => {
  return useMutation((record: RecipeRawProdsRequestBody) => {
    return apiClient.put(`/recipes/details/remove`, record);
  });
}

export const useAddRawProdsToRecipes = () => {
  return useMutation<AxiosResponse<AddRawProdsToRecipesResponse[]>, unknown, AddRawProdsToRecipesRequestBody>(
    (body: AddRawProdsToRecipesRequestBody) => {
      return apiClient.post<AddRawProdsToRecipesResponse[]>(`/recipes/details`, body);
    }
  );
};

export const useRemoveRawProdsToRecipes = () => {
  return useMutation<AxiosResponse<RemoveRawProdsToRecipesResponse[]>, unknown, RemoveRawProdsToRecipesRequestBody>(
    (body: RemoveRawProdsToRecipesRequestBody) => {
      return apiClient.post<RemoveRawProdsToRecipesResponse[]>(`/recipes/details/remove`, body);
    }
  );
};

export const useCopyRecipes = () => {
  return useMutation((record: CopyRecipeRequestBody) => {
    return apiClient.post(`/recipes`, record);
  });
};


export const useDeleteRecipes = () => {
  return useQuery<AxiosResponse<DeleteRecipesResponse[]>>({
    queryKey: 'DeleteRecipes',
    queryFn: () => apiClient.get<DeleteRecipesResponse[]>('/recipes/recipes-info')
  });
};

  export const useDeleteRecipeRecords = () => {
    return useMutation((record: DeleteMultipleRecipes) => {
      return apiClient.post('/recipes/recipes-delete', record)
    });
  };




